/**
 * Produkte actions.
 * @module actions/Produkt
 */

import {
  GET_PRODUKTE,
  GET_PRODUKTE_NAMES,
  GET_PRODUKTBYID,
  CREATE_PRODUKT,
  UPDATE_PRODUKT,
  DELETE_PRODUKT,
  CHECK_PRODUKTE,
} from '../../constants/ActionTypes';

import isEmpty from 'lodash/isEmpty';

/**
 * Get Produkt items.
 * @function getProdukte
 * @param {boolean} showDeleted Whether to show deleted Produkte or not
 * @returns {Object} Produkt action.
 */
export function getProdukte({ showDeleted = false, batchingParameters = {} }) {
  const {
    limit = 10,
    skip = 0,
    sort_by = '',
    asc = false,
    searchTerms = '',
    searchField = {},
    filters = {},
  } = batchingParameters;
  // Initialize an empty array to store query parameters
  const queryParams = [];
  if (sort_by) {
    queryParams.push(`sort_by=${sort_by}`);
  }
  if (searchTerms) {
    queryParams.push(`search=${searchTerms}`);
  }
  if (searchField.value) {
    queryParams.push(`search_kategorie=${searchField.value}`);
  }
  // Construct the pathSuffix by joining query parameters with '&'
  let pathSuffix = `?limit=${limit}&skip=${skip}&asc=${asc}`;
  if (queryParams.length > 0) {
    pathSuffix += `&${queryParams.join('&')}`;
  }

  //Apply filters from batching params
  Object.entries(filters).map((filter) => {
    const filterValue = filter[1];
    let filterValues;
    if (!isEmpty(filterValue)) {
      if (Array.isArray(filter[1])) {
        filterValues = filterValue.map((singleValue) => {
          return singleValue.value;
        });
      } else if (typeof filterValue === 'object') {
        filterValues = filterValue.value;
      } else if (typeof filterValue === 'number') {
        filterValues = filterValue;
      } else if (typeof filterValue === 'boolean') {
        if (filterValue === true) {
          filterValues = 1;
        } else {
          filterValues = 0;
        }
      }

      pathSuffix += `&${filter[0]}=${filterValues}`;
    }
    return true;
  });

  if (showDeleted) {
    return {
      type: GET_PRODUKTE,
      request: {
        op: 'get',
        path: `/@produkte/deleted${pathSuffix}`,
      },
    };
  } else {
    return {
      type: GET_PRODUKTE,
      request: {
        op: 'get',
        path: `/@produkte${pathSuffix}`,
      },
    };
  }
}

/**
 * Get Produkt names and ids.
 * @function getProdukteNames
 * @returns {Object} Produkt action.
 */
export function getProdukteNames() {
  return {
    type: GET_PRODUKTE_NAMES,
    request: {
      op: 'get',
      path: `/@produkte/names`,
    },
  };
}

/**
 * Check Produkte for specific value in specific field.
 * @function checkProdukte
 * @param {string} field, specified field
 * @param {string} value, specified value
 * @returns {Object} Produkt action.
 */
export function checkProdukte(field, value) {
  return {
    type: CHECK_PRODUKTE,
    request: {
      op: 'get',
      path: `/@produkte_check/${field}=${value}`,
    },
  };
}

/**
 * Get Produkt item by ID.
 * @function getProduktByID
 * @param {string} id Produkt id
 * @returns {Object} Produkt action.
 */
export function getProduktByID(id) {
  return {
    type: GET_PRODUKTBYID,
    request: {
      op: 'get',
      path: `/@produkte/${id}`,
    },
  };
}

/**
 * Create a new Produkt.
 * @function createProdukt
 * @param {Object} formData The data of the new Produkt
 * @returns {Object} Produkt action.
 */
export function createProdukt(formData) {
  return {
    type: CREATE_PRODUKT,
    request: {
      op: 'post',
      path: '/@produkte',
      data: formData,
    },
  };
}

/**
 * Update a Produkt.
 * @function updateProdukt
 * @param {string} id The id of the Produkt to update
 * @param {Object} formData The data of the updated Produkt
 * @returns {Object} Produkt action.
 */
export function updateProdukt(id, formData) {
  return {
    type: UPDATE_PRODUKT,
    request: {
      op: 'patch',
      path: `/@produkte/${id}`,
      data: formData,
    },
  };
}

/**
 * Delete a Produkt.
 * @function deleteProdukt
 * @param {string} id The id of the Produkt to delete
 * @returns {Object} Success Message.
 */
export function deleteProdukt(id) {
  return {
    type: DELETE_PRODUKT,
    request: {
      op: 'del',
      path: `/@produkte/${id}`,
    },
  };
}
export function deleteProduktForce(id) {
  return {
    type: DELETE_PRODUKT,
    request: {
      op: 'put',
      path: `/@produkte_force_delete/${id}`,
    },
  };
}
